/**
 * StudioPlatformIcon — small inline-SVG icons for the channels that
 * Founderboard Studios delivers to. We don't use a brand-icon npm package
 * to keep bundle size small + colours fully controllable.
 *
 * Each platform also exposes a human label + a brand colour the calendar
 * uses for the deliverable tile.
 */
import React from 'react';

export const STUDIO_PLATFORMS = [
  { id: 'instagram_reel',    label: 'Instagram Reel',     color: '#E1306C' },
  { id: 'instagram_post',    label: 'Instagram Post',     color: '#E1306C' },
  { id: 'instagram_story',   label: 'Instagram Story',    color: '#E1306C' },
  { id: 'linkedin_post',     label: 'LinkedIn Post',      color: '#0A66C2' },
  { id: 'linkedin_article',  label: 'LinkedIn Article',   color: '#0A66C2' },
  { id: 'twitter_post',      label: 'X (Twitter) Post',   color: '#111111' },
  { id: 'youtube_video',     label: 'YouTube Video',      color: '#FF0000' },
  { id: 'youtube_short',     label: 'YouTube Short',      color: '#FF0000' },
  { id: 'facebook_post',     label: 'Facebook Post',      color: '#1877F2' },
  { id: 'blog',              label: 'Blog Article',       color: '#F97316' },
  { id: 'newsletter',        label: 'Newsletter',         color: '#8B5CF6' },
  { id: 'email',             label: 'Email Campaign',     color: '#8B5CF6' },
  { id: 'website',           label: 'Website Update',     color: '#0EA5E9' },
  { id: 'podcast',           label: 'Podcast',            color: '#7C3AED' },
  { id: 'press_release',     label: 'Press Release',      color: '#6B7280' },
  { id: 'deck',              label: 'Pitch / Deck',       color: '#EAB308' },
  { id: 'other',             label: 'Other',              color: '#6B7280' },
];

const META_BY_ID = Object.fromEntries(STUDIO_PLATFORMS.map((p) => [p.id, p]));

export const getPlatformMeta = (id) => META_BY_ID[id] || META_BY_ID.other;

/** Glyph paths only — caller controls fill + size. */
const GLYPHS = {
  instagram: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4.2" fill="white" />
      <circle cx="12" cy="12" r="2.6" />
      <circle cx="17.5" cy="6.5" r="1.1" fill="white" />
    </>
  ),
  linkedin: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="3" />
      <rect x="5" y="9.5" width="3" height="9" fill="white" />
      <circle cx="6.5" cy="6.5" r="1.7" fill="white" />
      <path d="M11 18.5v-9h2.7v1.3c.5-.9 1.7-1.5 3-1.5 2.3 0 3.3 1.5 3.3 4v5.2h-3v-4.6c0-1.2-.4-1.9-1.5-1.9-1 0-1.5.7-1.5 1.9v4.6H11z" fill="white" />
    </>
  ),
  twitter: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="3" />
      <path d="M17.5 6h2l-4.5 5.4 5.4 7.6h-4.2l-3.3-4.6L8.7 19H6.6l4.9-5.7L6 6h4.3l3 4.2L17.5 6z" fill="white" />
    </>
  ),
  youtube: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <path d="M10 9l6 3-6 3V9z" fill="white" />
    </>
  ),
  facebook: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="3" />
      <path d="M14.5 22v-7h2.4l.3-3h-2.7v-1.7c0-.9.2-1.5 1.5-1.5h1.5V6.1c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8V12H9v3h2.6v7h2.9z" fill="white" />
    </>
  ),
  blog: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M6.5 8.5h11M6.5 12h11M6.5 15.5h7" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </>
  ),
  email: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M4 7l8 6 8-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <ellipse cx="12" cy="12" rx="4" ry="9.5" stroke="white" strokeWidth="1.2" fill="none" />
      <path d="M3 12h18M5 8h14M5 16h14" stroke="white" strokeWidth="1.2" fill="none" />
    </>
  ),
  podcast: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="12" r="2" fill="white" />
      <path d="M8 8a5 5 0 0 1 8 0M6 6a8 8 0 0 1 12 0" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </>
  ),
  press: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <rect x="6" y="7" width="6" height="4" rx="1" fill="white" />
      <path d="M14 7.5h4M14 11h4M6 13.5h12M6 16h12" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </>
  ),
  deck: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2" />
      <path d="M2.5 14h19" stroke="white" strokeWidth="1.2" />
      <path d="M9.5 17v3h5v-3" stroke="white" strokeWidth="1.2" fill="none" />
      <path d="M7 11l3-3 2 2 3-3 2 2" stroke="white" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  dot: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="9" />
      <circle cx="12" cy="12" r="2.5" fill="white" />
    </>
  ),
};

const PLATFORM_TO_GLYPH = {
  instagram_reel: 'instagram',
  instagram_post: 'instagram',
  instagram_story: 'instagram',
  linkedin_post: 'linkedin',
  linkedin_article: 'linkedin',
  twitter_post: 'twitter',
  youtube_video: 'youtube',
  youtube_short: 'youtube',
  facebook_post: 'facebook',
  blog: 'blog',
  newsletter: 'email',
  email: 'email',
  website: 'globe',
  podcast: 'podcast',
  press_release: 'press',
  deck: 'deck',
  other: 'dot',
};

/**
 * <StudioPlatformIcon platform="instagram_reel" size={20} />
 * Renders a solid coloured tile with a white glyph — recognisable at small sizes.
 */
export const StudioPlatformIcon = ({ platform, size = 18, rounded = 6, className = '', title }) => {
  const meta = getPlatformMeta(platform);
  const glyph = GLYPHS[PLATFORM_TO_GLYPH[meta.id]] || GLYPHS.dot;
  return (
    <span
      className={`inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size, borderRadius: rounded, backgroundColor: meta.color }}
      title={title || meta.label}
      data-testid={`platform-icon-${meta.id}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={meta.color}
        style={{ display: 'block' }}
        aria-hidden="true"
      >
        {glyph}
      </svg>
    </span>
  );
};

export default StudioPlatformIcon;
